import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '@material-ui/core/Input';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import FormHelperText from '@material-ui/core/FormHelperText';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import SendIcon from '@material-ui/icons/Send';
import SettingsIcon from '@material-ui/icons/Settings';
import Paper from '~/components/Paper';
import InputControl from '~/components/Input/InputControl';
import InputNumber from '~/components/Input/InputNumber';
import Form from '~/containers/Form';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import Request from '~/providers/Request';
import { AbilityContext } from '~/contexts/Can';
import Invitations from './Invitations';
import useStyles from './styles';

const PATH = '/admin/pre_registration_campaign';
const INVITATION_STATUSES = ['sent', 'delivered', 'undeliverable', 'failed', 'skipped'];
const SUMMARY_KEYS = ['requested', 'sent', 'failed', 'skipped', 'stopped_reason'];
const ACTIVE_RUN_STATUSES = ['queued', 'running'];
const RUN_POLL_MS = 3000;

const isRun = (data) => Boolean(data && data.id && data.status);
// Consulta que saiu antes do cancelamento não pode reabrir um envio que já terminou.
const keepFinished = (data) => (current) => (current && current.id === data.id && current.status === 'finished' ? current : data);
const minutesSince = (value) => Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60000));

const sumOf = (counts, keys) => keys.reduce((sum, key) => sum + Number((counts || {})[key] || 0), 0);

const NUMBER_GROUPS = [
  {
    key: 'audience',
    rows: [
      { key: 'pre_registered', value: (preview) => preview.pre_registered },
      { key: 'eligible_now', value: (preview) => preview.eligible_now, highlight: true },
    ],
  },
  {
    key: 'invitations',
    rows: INVITATION_STATUSES.map((status) => ({ key: status, value: (preview) => sumOf(preview.invitations, [status]) })),
  },
  {
    key: 'conversations',
    rows: [
      { key: 'awaiting_answer', value: (preview) => sumOf(preview.conversations, ['started', 'awaiting_consent']) },
      { key: 'awaiting_choice', value: (preview) => sumOf(preview.conversations, ['awaiting_patient_choice']) },
      {
        key: 'awaiting_validation',
        value: (preview) => sumOf(preview.conversations, ['awaiting_birth_month', 'awaiting_surname', 'awaiting_cpf_digits', 'awaiting_mother_name']),
      },
      { key: 'declined', value: (preview) => sumOf(preview.conversations, ['declined']) },
      { key: 'activated', value: (preview) => preview.activated, highlight: true },
      { key: 'cancelled', value: (preview) => sumOf(preview.conversations, ['failed']) },
      { key: 'opted_out', value: (preview) => sumOf(preview.conversations, ['opted_out']) },
      { key: 'no_response', value: (preview) => preview.no_response },
    ],
  },
];

const numberInputProps = { styleInput: { marginTop: 30 }, precision: 0, showMask: false, config: { allowNegative: false } };

const SETTINGS_FIELDS = ['template_name', 'support_phone'];
const EMPTY_SETTINGS = { template_name: '', support_phone: '' };

const settingsParams = (settings) => ({
  data: { attributes: SETTINGS_FIELDS.reduce((attributes, field) => ({ ...attributes, [field]: settings[field] }), {}) },
});

const TIME_ZONE = 'America/Sao_Paulo';
const dayInTimeZone = (date) => date.toLocaleDateString('pt-BR', { timeZone: TIME_ZONE });

const formatSlot = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const time = date.toLocaleTimeString('pt-BR', { timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit' });
  if (dayInTimeZone(date) === dayInTimeZone(new Date())) return time;
  return `${date.toLocaleDateString('pt-BR', { timeZone: TIME_ZONE, day: '2-digit', month: '2-digit' })} ${time}`;
};

const formatCount = (value) => Number(value || 0).toLocaleString('pt-BR');

function PreRegistrationCampaign() {
  const classes = useStyles();
  const { t } = useTranslation();
  const tr = (key, options) => t(`preRegistrationCampaign.extras.${key}`, options);
  const trAttr = (attr) => t(`preRegistrationCampaign.attributes.${attr}`);
  const canEdit = useContext(AbilityContext).can('edit', 'WhatsappCampaign');

  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [quantity, setQuantity] = useState(null);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmBatch, setConfirmBatch] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [sending, setSending] = useState(false);
  const [run, setRun] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const unmounted = useRef(false);
  const sendingRef = useRef(false);
  const wasActive = useRef(false);

  useEffect(() => () => { unmounted.current = true; }, []);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const response = await Request.get(`${PATH}/preview`, {}, { normalizeJSON: false });
      setPreview(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const response = await Request.get(`${PATH}/settings`, {}, { normalizeJSON: false });
      setSettings(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const loadRun = useCallback(async () => {
    try {
      const response = await Request.get(`${PATH}/send_runs/current`, {}, { normalizeJSON: false, disableToastErrors: true });
      // No 401 o Request renova o token e repete sem o config, e a resposta volta sem o envio.
      if (!unmounted.current && isRun(response.data)) setRun(keepFinished(response.data));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadPreview();
    loadRun();
  }, [loadSettings, loadPreview, loadRun]);

  const handleChange = (attrs) => {
    setSettings({ ...settings, ...attrs });
    setFieldErrors({});
  };

  async function saveSettings() {
    setConfirmSave(false);
    try {
      const response = await Request.put(
        `${PATH}/settings`,
        settingsParams(settings),
        {},
        { normalizeJSON: false, disableToastErrors: true }
      );
      setSettings(response.data);
      setFieldErrors({});
      toast.success(tr('saved'));
      loadPreview();
    } catch (e) {
      const errors = (e && e.list) || [];
      const byField = {};
      errors.forEach(({ title, detail, source }) => {
        const field = source && source.pointer;
        if (SETTINGS_FIELDS.includes(field)) byField[field] = detail || title;
        else toast.error(detail || title);
      });
      setFieldErrors(byField);
      if (!errors.length) toast.error(t('request-error'));
    }
  }

  const hasDailyUsage = Boolean(preview) && Number.isInteger(preview.remaining_now);
  const dailyLimitReached = hasDailyUsage && preview.remaining_now === 0;
  const sendable = preview ? Math.min(preview.eligible_now, hasDailyUsage ? preview.remaining_now : Infinity) : 0;
  const batchTarget = Number.isInteger(quantity) && quantity >= 1 ? Math.min(quantity, sendable) : 0;

  // Código sem texto próprio na API chega como detail igual ao code.
  const errorMessage = ({ detail, code }) => (detail === code ? tr(`reasons.${code}`, { defaultValue: detail }) : detail);

  async function sendBatch() {
    setConfirmBatch(false);
    // Clique duplo na confirmação não pode abrir um segundo envio em paralelo.
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const response = await Request.post(
        `${PATH}/send_runs`,
        { data: { attributes: { quantity: batchTarget } } },
        {},
        { normalizeJSON: false, disableToastErrors: true }
      );
      if (isRun(response.data)) setRun(response.data);
      else loadRun();
    } catch (e) {
      if (unmounted.current) return;
      const error = e && e.list && e.list[0];
      toast.error(error ? errorMessage(error) : t('request-error'));
      loadRun();
      loadPreview();
    } finally {
      sendingRef.current = false;
      if (!unmounted.current) setSending(false);
    }
  }

  async function cancelRun() {
    setConfirmCancel(false);
    try {
      const response = await Request.post(
        `${PATH}/send_runs/${run.id}/cancel`,
        {},
        {},
        { normalizeJSON: false, disableToastErrors: true }
      );
      if (isRun(response.data)) setRun(response.data);
    } catch (e) {
      const error = e && e.list && e.list[0];
      toast.error(error ? errorMessage(error) : t('request-error'));
    }
  }

  const summaryValue = (key, value) => {
    if (value === null || value === undefined) return '-';
    if (key === 'skipped') {
      const parts = Object.entries(value).map(([reason, count]) => `${tr(`reasons.${reason}`, { defaultValue: reason })}: ${count}`);
      return parts.length ? parts.join(' · ') : '0';
    }
    if (key === 'stopped_reason') return tr(`summary.${value}`, { defaultValue: value });
    return `${value}`;
  };

  const active = Boolean(run) && ACTIVE_RUN_STATUSES.includes(run.status);
  const activeRunId = active ? run.id : null;

  useEffect(() => {
    if (!activeRunId) return undefined;
    let timer = null;
    let stopped = false;
    const poll = async () => {
      await loadRun();
      if (!stopped) timer = setTimeout(poll, RUN_POLL_MS);
    };
    timer = setTimeout(poll, RUN_POLL_MS);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [activeRunId, loadRun]);

  useEffect(() => {
    if (wasActive.current && !active && run) {
      if (run.stopped_reason === 'batch_full') toast.success(tr('batchDone'));
      else toast.warn(`${tr('summary.stopped_reason')}: ${summaryValue('stopped_reason', run.stopped_reason)}`);
      setConfirmCancel(false);
      setListVersion((version) => version + 1);
      loadPreview();
    }
    wasActive.current = active;
  }, [active, run, loadPreview]);

  const fieldHelp = (field, help) => (fieldErrors[field]
    ? <FormHelperText error>{fieldErrors[field]}</FormHelperText>
    : <Typography className={classes.help}>{help}</Typography>);

  const statusActions = (
    <div className={classes.paperActions}>
      <Button variant="outlined" size="small" className={classes.smallButton} disabled={previewLoading} onClick={loadPreview}>
        {tr('refresh')}
      </Button>
    </div>
  );

  return (
    <>
      <Paper Icon={WhatsAppIcon} iconColor="green" title={tr('statusTitle')} loading={previewLoading && !preview} actions={statusActions}>
        <div className={classes.section}>
          {previewLoading && preview && <CircularProgress size={18} className={classes.inlineProgress} />}
          {preview && (
            <>
              <GridContainer>
                {NUMBER_GROUPS.map((group) => (
                  <GridItem key={group.key} xs={12} md={4}>
                    <Table size="small" className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{tr(`groups.${group.key}`)}</TableCell>
                          <TableCell align="right">{tr('people')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.rows.map((row) => (
                          <TableRow key={row.key} className={row.highlight ? classes.highlightRow : undefined}>
                            <TableCell>{tr(`numbers.${row.key}`)}</TableCell>
                            <TableCell align="right">{Number(row.value(preview) || 0).toLocaleString('pt-BR')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </GridItem>
                ))}
              </GridContainer>
              {hasDailyUsage && (
                <Typography className={classes.help}>
                  {tr('sentLast24h', { sent: formatCount(preview.sent_last_24h), limit: formatCount(preview.daily_limit) })}
                  {' · '}
                  {tr('remainingNow', { remaining: formatCount(preview.remaining_now) })}
                  {dailyLimitReached && preview.next_slot_at && ` · ${tr('nextSlot', { time: formatSlot(preview.next_slot_at) })}`}
                </Typography>
              )}
            </>
          )}
        </div>
      </Paper>

      <Form onSubmit={async () => setConfirmSave(true)} isPersisted showCancel={false} showFooter={canEdit}>
        <Paper Icon={SettingsIcon} iconColor="blue" title={tr('settingsTitle')} loading={settingsLoading}>
          <div className={classes.section}>
            <GridContainer>
              <GridItem xs={12} md={4}>
                <InputControl
                  name="template_name"
                  value={settings.template_name || ''}
                  label={trAttr('template_name')}
                  disabled={!canEdit}
                  inputComponent={Input}
                  onChange={handleChange}
                  InputProps={{ error: Boolean(fieldErrors.template_name), inputProps: { maxLength: 100 } }}
                />
                {fieldHelp('template_name', tr('templateHelp'))}
              </GridItem>
              <GridItem xs={12} md={4}>
                <InputControl
                  name="support_phone"
                  value={settings.support_phone || ''}
                  label={trAttr('support_phone')}
                  disabled={!canEdit}
                  inputComponent={Input}
                  onChange={handleChange}
                  InputProps={{ error: Boolean(fieldErrors.support_phone), inputProps: { maxLength: 30 } }}
                />
                {fieldHelp('support_phone', tr('supportPhoneHelp'))}
              </GridItem>
            </GridContainer>
          </div>
        </Paper>
      </Form>

      <Paper Icon={SendIcon} iconColor="green" title={tr('batchTitle')} loading={previewLoading && !preview && !run}>
        <div className={classes.section}>
          <div className={classes.batchRow}>
            <div className={classes.batchField}>
              <InputControl
                name="quantity"
                value={quantity}
                label={trAttr('quantity')}
                inputComponent={InputNumber}
                disabled={sending || active}
                onChange={({ quantity: value }) => setQuantity(value)}
                InputProps={numberInputProps}
              />
            </div>
            <Button
              variant="contained"
              color="primary"
              className={`${classes.smallButton} ${classes.batchButton}`}
              disabled={!batchTarget || sending || active || previewLoading || dailyLimitReached}
              onClick={() => setConfirmBatch(true)}
            >
              {tr('sendBatch')}
            </Button>
            {active && (
              <Button
                variant="outlined"
                className={`${classes.smallButton} ${classes.batchButton}`}
                disabled={Boolean(run.cancel_requested_at) && !run.stale}
                onClick={() => setConfirmCancel(true)}
              >
                {tr('cancelRun')}
              </Button>
            )}
            {(sending || active) && <CircularProgress size={18} className={classes.batchButton} />}
          </div>
          {dailyLimitReached && (
            <Typography className={classes.help}>
              {tr('dailyLimitReached', { limit: formatCount(preview.daily_limit), time: formatSlot(preview.next_slot_at) })}
            </Typography>
          )}
          {active && (
            <Typography className={classes.help}>
              {run.status === 'running' ? tr(run.cancel_requested_at ? 'runStopping' : 'runRunning') : tr('runQueued')}
              {run.status === 'queued' && run.stale && ` · ${tr('runStale', { minutes: minutesSince(run.created_at) })}`}
              {run.status === 'running' && run.stale && ` · ${tr('runStalled', { minutes: minutesSince(run.heartbeat_at) })}`}
            </Typography>
          )}

          {run && (
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  {SUMMARY_KEYS.map((key) => <TableCell key={key}>{tr(`summary.${key}`)}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {SUMMARY_KEYS.map((key) => <TableCell key={key}>{summaryValue(key, run[key])}</TableCell>)}
                </TableRow>
              </TableBody>
            </Table>
          )}
        </div>
      </Paper>

      <Invitations version={listVersion} />

      <Dialog open={confirmSave} onClose={() => setConfirmSave(false)}>
        <DialogTitle>{tr('saveTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{tr('saveConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSave(false)}>{t('form-cancel')}</Button>
          <Button color="primary" variant="contained" onClick={saveSettings}>{tr('confirm')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmBatch} onClose={() => setConfirmBatch(false)}>
        <DialogTitle>{tr('sendBatch')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{tr('sendBatchConfirm', { count: batchTarget })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBatch(false)}>{t('form-cancel')}</Button>
          <Button color="primary" variant="contained" onClick={sendBatch}>{tr('sendBatch')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmCancel && active} onClose={() => setConfirmCancel(false)}>
        <DialogTitle>{tr('cancelRun')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{tr('cancelRunConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancel(false)}>{t('form-cancel')}</Button>
          <Button color="primary" variant="contained" onClick={cancelRun}>{tr('cancelRun')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PreRegistrationCampaign;
